import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { RecoilState, useRecoilState } from 'recoil';

import { OrderByModalTypes } from '../../components/OrderByModal';
import { TableHeadItemRecurse } from '../../components/TableHead';

interface DynamicOrdinationContextReceivedData {
  orderAtom: RecoilState<OrderByModalTypes.OrderByAtom>;
  tableHead: TableHeadItemRecurse[];
}

interface DynamicOrdinationContextData extends DynamicOrdinationContextReceivedData {
  orderAtom: RecoilState<OrderByModalTypes.OrderByAtom>;
  appliedOrderBy: OrderByModalTypes.OrderByAtom['appliedOrderBy'];
  availableOrderBy: OrderByModalTypes.OrderByAtom['availableOrderBy'];
  selectedOrderBy: OrderByModalTypes.OrderByAtom['selectedOrderBy'];
  handleAutoOrderBy: (value?: string) => void;
  handleOrderBy: (value?: string, forceDirection?: 'asc' | 'desc') => void;
}

interface Props extends DynamicOrdinationContextReceivedData {
  children: React.ReactElement;
}

const DynamicOrdinationContext = createContext<DynamicOrdinationContextData>(
  {} as DynamicOrdinationContextData
);

const DynamicOrdinationProvider: React.FC<Props> = ({ children, tableHead, orderAtom }) => {
  const [{ appliedOrderBy, availableOrderBy, selectedOrderBy }, setAssetsFiltersAtom] =
    useRecoilState(orderAtom);

  const orderByOptions = useMemo(() => {
    const headerItems = tableHead;

    return headerItems
      .filter(({ orderValue }) => !!orderValue)
      .map(({ orderValue, title }) => {
        return {
          label: title,
          value: orderValue as string,
        };
      });
  }, [tableHead]);

  const handleOrderBy = (value?: string, forceDirection?: 'asc' | 'desc') => {
    if (!value) {
      setAssetsFiltersAtom((prev) => ({
        ...prev,
        appliedOrderBy: undefined,
      }));
      return;
    }
    setAssetsFiltersAtom((prev) => ({
      ...prev,
      appliedOrderBy: {
        value,
        direction: forceDirection || 'asc',
      },
    }));
  };

  const handleAutoOrderBy = (value?: string) => {
    const isSameCurrent = appliedOrderBy?.value === value;

    const getDirection = () => {
      if (!isSameCurrent) {
        return 'asc';
      }
      return appliedOrderBy?.direction === 'asc' ? 'desc' : 'asc';
    };

    handleOrderBy(value, getDirection());
  };

  useEffect(() => {
    setAssetsFiltersAtom((prev) => ({
      ...prev,
      availableOrderBy: orderByOptions,
    }));
  }, [orderByOptions, setAssetsFiltersAtom]);

  return (
    <DynamicOrdinationContext.Provider
      value={{
        tableHead,
        orderAtom,
        appliedOrderBy,
        availableOrderBy,
        selectedOrderBy,
        handleAutoOrderBy,
        handleOrderBy,
      }}>
      {children}
    </DynamicOrdinationContext.Provider>
  );
};

const useDynamicOrdinationContext = (): DynamicOrdinationContextData => {
  const context = useContext(DynamicOrdinationContext);

  if (!context) {
    throw new Error('useDynamicOrdinationContext must be used within an DynamicOrdinationContext');
  }
  return context;
};

export { DynamicOrdinationProvider, useDynamicOrdinationContext, DynamicOrdinationContextData };
